import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { testsAPI } from '../services/api';

function Dashboard() {
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null); // { testId, step: 1 oder 2 }
    const [deleting, setDeleting] = useState(false);
    
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadTests();
    }, []);

    const loadTests = async () => {
        try {
            const response = await testsAPI.getAllTests();
            setTests(response.data);
        } catch (err) {
            setError('Fehler beim Laden der Tests');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleDeleteClick = (testId) => {
        setDeleteConfirm({ testId, step: 1 });
    };

    const handleDeleteConfirmStep1 = (testId) => {
        setDeleteConfirm({ testId, step: 2 });
    };

    const handleDeleteConfirmStep2 = async (testId) => {
        setDeleting(true);
        setError('');

        try {
            await testsAPI.deleteTest(testId);
            setTests(tests.filter(t => t.id !== testId));
            setDeleteConfirm(null);
        } catch (err) {
            setError('Fehler beim Löschen des Tests');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm(null);
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1>Dashboard</h1>
                <div style={styles.userInfo}>
                    <span>Willkommen, {user?.name}</span>
                    <button onClick={handleLogout} style={styles.logoutButton}>
                        Abmelden
                    </button>
                </div>
            </div>

            <div style={styles.actions}>
                <button 
                    onClick={() => navigate('/create-test')}
                    style={styles.createButton}
                >
                    + Neuen Test erstellen
                </button>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            {loading ? (
                <div style={styles.loading}>Lädt...</div>
            ) : tests.length === 0 ? (
                <div style={styles.empty}>
                    <p>Noch keine Tests erstellt.</p>
                    <p>Erstelle deinen ersten Test!</p>
                </div>
            ) : (
                <div style={styles.testsGrid}>
                    {tests.map(test => (
                        <div key={test.id} style={styles.testCard}>
                            <h3>{test.title}</h3>
                            <p style={styles.description}>{test.description}</p>
                            
                            <div style={styles.testInfo}>
                                <span>📝 Passwort: <strong>{test.test_password}</strong></span>
                                {test.time_limit && (
                                    <span>⏱️ {test.time_limit} Min.</span>
                                )}
                                <span>📊 {test.submission_count} Abgabe(n)</span>
                            </div>

                            {deleteConfirm?.testId === test.id ? (
                                <div style={styles.deleteConfirmBox}>
                                    {deleteConfirm.step === 1 ? (
                                        <>
                                            <p style={styles.deleteWarning}>
                                                ⚠️ Möchtest du diesen Test wirklich löschen?
                                            </p>
                                            <div style={styles.deleteButtons}>
                                                <button 
                                                    onClick={() => handleDeleteConfirmStep1(test.id)}
                                                    style={styles.deleteConfirmButton}
                                                >
                                                    Ja, löschen
                                                </button>
                                                <button 
                                                    onClick={handleDeleteCancel}
                                                    style={styles.cancelButton}
                                                >
                                                    Abbrechen
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p style={styles.deleteFinalWarning}>
                                                🚨 <strong>LETZTE WARNUNG!</strong><br/>
                                                Der Test "{test.title}" und alle {test.submission_count} Abgabe(n) werden unwiderruflich gelöscht!<br/>
                                                <strong>Diese Aktion kann NICHT rückgängig gemacht werden!</strong>
                                            </p>
                                            <div style={styles.deleteButtons}>
                                                <button 
                                                    onClick={() => handleDeleteConfirmStep2(test.id)}
                                                    disabled={deleting}
                                                    style={styles.deleteFinalButton}
                                                >
                                                    {deleting ? 'Wird gelöscht...' : 'Endgültig löschen'}
                                                </button>
                                                <button 
                                                    onClick={handleDeleteCancel}
                                                    disabled={deleting}
                                                    style={styles.cancelButton}
                                                >
                                                    Abbrechen
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div style={styles.cardActions}>
                                    <button 
                                        onClick={() => navigate(`/test/${test.id}`)}
                                        style={styles.viewButton}
                                    >
                                        Details
                                    </button>
                                    <button 
                                        onClick={() => navigate(`/test/${test.id}/submissions`)}
                                        style={styles.submissionsButton}
                                    >
                                        Abgaben ({test.submission_count})
                                    </button>
                                    <button 
                                        onClick={() => handleDeleteClick(test.id)}
                                        style={styles.deleteButton}
                                    >
                                        🗑️ Löschen
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const styles = {
    container: {
        padding: '2rem',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        paddingBottom: '1rem',
        borderBottom: '2px solid #eee',
    },
    userInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
    },
    logoutButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#dc3545',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    actions: {
        marginBottom: '2rem',
    },
    createButton: {
        padding: '0.75rem 1.5rem',
        backgroundColor: '#28a745',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    loading: {
        textAlign: 'center',
        padding: '3rem',
        fontSize: '1.2rem',
        color: '#666',
    },
    empty: {
        textAlign: 'center',
        padding: '3rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
    },
    error: {
        backgroundColor: '#fee',
        color: '#c33',
        padding: '1rem',
        borderRadius: '4px',
        marginBottom: '1rem',
    },
    testsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '1.5rem',
    },
    testCard: {
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    description: {
        color: '#666',
        marginTop: '0.5rem',
    },
    testInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        margin: '1rem 0',
        padding: '0.75rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        fontSize: '0.9rem',
    },
    cardActions: {
        display: 'flex',
        gap: '0.5rem',
        marginTop: '1rem',
        flexWrap: 'wrap',
    },
    viewButton: {
        flex: 1,
        minWidth: '80px',
        padding: '0.5rem',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    submissionsButton: {
        flex: 1,
        minWidth: '120px',
        padding: '0.5rem',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    deleteButton: {
        padding: '0.5rem 0.75rem',
        backgroundColor: '#dc3545',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
    },
    deleteConfirmBox: {
        marginTop: '1rem',
        padding: '1rem',
        backgroundColor: '#fff3cd',
        borderRadius: '4px',
        border: '2px solid #ffc107',
    },
    deleteWarning: {
        marginBottom: '1rem',
        color: '#856404',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    deleteFinalWarning: {
        marginBottom: '1rem',
        color: '#721c24',
        fontWeight: 'bold',
        textAlign: 'center',
        backgroundColor: '#f8d7da',
        padding: '1rem',
        borderRadius: '4px',
        border: '2px solid #dc3545',
        lineHeight: '1.6',
    },
    deleteButtons: {
        display: 'flex',
        gap: '0.5rem',
        justifyContent: 'center',
    },
    deleteConfirmButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#ffc107',
        color: '#000',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
    },
    deleteFinalButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#dc3545',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
    },
    cancelButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
    },
};

export default Dashboard;
